/**
 * Parking from actual ground dwell. Catalog amount is a per-calendar-day
 * (night) rate. Quote stays a 1-day estimate until touchdown + later
 * departure are both logged (or SimConnect records both transitions).
 *
 * Do not invent overnight from wall-clock while the desk was closed, and do
 * not lend one session's SimConnect dwell to a ticket the sim never flew —
 * the dwell has to belong to the field that ticket actually sat at.
 */
import { icaoKey } from "./curfew";
import { hydrateCurrency, lookupOpsFee, weightClassForType, type CurrencyCode, type MoneyCatalogs } from "./catalogs";
import {
  completionBlock,
  fieldEverVisited,
  groundTicket,
  type GroundingHold,
  type TicketGrounding,
} from "./flightGrounding";
import { ledgerIdempotencyKey, replaceTripParkingTxn, voidTripKindTxn } from "./ledger";
import type { FusedPosition } from "./position";
import { sampleIsAirborne, type SimPhaseSample } from "./simDraft";
import type { AircraftSpec, CompanyAccount, FlightLogEntry, Mission, SceneryAirport } from "./types";

/** Catalog parking row is one night / calendar day. */
export const PARKING_DAY_HOURS = 24;
/** Same-day quick turn — no overnight parking. */
export const PARKING_QUICK_TURN_H = 2;

export type GroundDwell = {
  icao: string;
  startedAtUtc: string;
  endedAtUtc: string;
  hours: number;
  source: "flight_log" | "simconnect";
};

function asMs(iso: string | undefined | null): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : null;
}

function hoursBetween(startIso: string, endIso: string): number | null {
  const a = asMs(startIso);
  const b = asMs(endIso);
  if (a == null || b == null || b <= a) return null;
  return Math.round(((b - a) / 3600_000) * 10) / 10;
}

export function parkingDaysFromDwellHours(hours: number): number {
  if (!Number.isFinite(hours) || hours < PARKING_QUICK_TURN_H) return 0;
  return Math.max(1, Math.ceil(hours / PARKING_DAY_HOURS));
}

export function parkingCadFromDays(dayRateCad: number, days: number): number {
  if (!(dayRateCad > 0) || days <= 0) return 0;
  return Math.round(dayRateCad * days * 100) / 100;
}

export function parkingDayRateCad(
  catalogs: MoneyCatalogs | undefined,
  icao: string,
  aircraft?: Pick<AircraftSpec, "icaoType">,
): number {
  const cls = weightClassForType(aircraft?.icaoType);
  return lookupOpsFee(catalogs, icao, "parking", 0, cls);
}

export function parkingMemo(icao: string, dwell?: GroundDwell | null, days?: number): string {
  const field = icaoKey(icao) || icao;
  if (dwell && days != null) {
    return `Parking ${field} · ${dwell.hours} h dwell · ${days} day${days === 1 ? "" : "s"}`;
  }
  if (dwell) {
    return `Parking ${field} · ${dwell.hours} h dwell`;
  }
  return `Parking ${field}`;
}

function sameReg(a?: string, b?: string): boolean {
  return Boolean(a && b && a.trim().toUpperCase() === b.trim().toUpperCase());
}

/**
 * Touchdown → next departure at the same field, from flight logs only.
 * Both endpoints must already be recorded.
 */
export function dwellAfterArrivalLog(input: {
  arrival: Pick<FlightLogEntry, "destIcao" | "arrivedAtUtc" | "registration" | "tripId" | "departedAtUtc">;
  logs: ReadonlyArray<FlightLogEntry>;
  missions?: ReadonlyArray<Pick<Mission, "id" | "aircraftId" | "originIcao" | "registration">>;
  aircraftId?: string;
}): GroundDwell | null {
  const startedAtUtc = input.arrival.arrivedAtUtc;
  const icao = icaoKey(input.arrival.destIcao);
  if (!startedAtUtc || !icao) return null;
  const startMs = asMs(startedAtUtc);
  if (startMs == null) return null;

  let endedAtUtc: string | undefined;
  for (const log of input.logs) {
    if (log.tripId === input.arrival.tripId) continue;
    if (!log.departedAtUtc) continue;
    const dep = asMs(log.departedAtUtc);
    if (dep == null || dep <= startMs) continue;
    const sameTail =
      sameReg(log.registration, input.arrival.registration) ||
      Boolean(
        input.aircraftId &&
          input.missions?.some((m) => m.id === log.tripId && m.aircraftId === input.aircraftId),
      );
    if (!sameTail && input.arrival.registration) continue;
    const origin = icaoKey(log.originIcao);
    if (origin && origin !== icao) continue;
    if (!endedAtUtc || dep < (asMs(endedAtUtc) ?? Number.POSITIVE_INFINITY)) {
      endedAtUtc = log.departedAtUtc;
    }
  }
  if (!endedAtUtc) return null;
  const hours = hoursBetween(startedAtUtc, endedAtUtc);
  if (hours == null) return null;
  return { icao, startedAtUtc, endedAtUtc, hours, source: "flight_log" };
}

/**
 * Same-session SimConnect: last airborne→ground then a later ground→air.
 * Uses recorded sample timestamps only — not wall-clock while closed.
 */
export function dwellFromSimHistory(
  history: ReadonlyArray<SimPhaseSample>,
  icao?: string,
): GroundDwell | null {
  if (history.length < 2) return null;
  const rows = [...history].sort((a, b) => (asMs(a.atUtc) ?? 0) - (asMs(b.atUtc) ?? 0));
  let landedAt: string | undefined;
  let airborneAt: string | undefined;
  for (let i = 1; i < rows.length; i++) {
    const prev = rows[i - 1]!;
    const cur = rows[i]!;
    if (sampleIsAirborne(prev) && !sampleIsAirborne(cur)) {
      landedAt = cur.atUtc;
      airborneAt = undefined;
    }
    if (landedAt && !sampleIsAirborne(prev) && sampleIsAirborne(cur)) {
      airborneAt = cur.atUtc;
    }
  }
  if (!landedAt || !airborneAt) return null;
  const hours = hoursBetween(landedAt, airborneAt);
  if (hours == null) return null;
  return {
    icao: icaoKey(icao) || "",
    startedAtUtc: landedAt,
    endedAtUtc: airborneAt,
    hours,
    source: "simconnect",
  };
}

export function resolveParkingDwell(input: {
  mission: Pick<Mission, "id" | "destIcao" | "aircraftId" | "registration">;
  logs: ReadonlyArray<FlightLogEntry>;
  missions?: ReadonlyArray<Mission>;
  simHistory?: ReadonlyArray<SimPhaseSample>;
  /**
   * Field the SimConnect history belongs to. When the sim is sitting somewhere
   * else, its dwell is another flight's — this ticket gets no sim fallback.
   */
  simAtIcao?: string | null;
}): GroundDwell | null {
  const arrival = input.logs.find((l) => l.tripId === input.mission.id);
  const fromLog = arrival
    ? dwellAfterArrivalLog({
        arrival,
        logs: input.logs,
        missions: input.missions,
        aircraftId: input.mission.aircraftId,
      })
    : null;
  if (fromLog) return fromLog;
  if (!input.simHistory?.length) return null;
  const simField = icaoKey(input.simAtIcao);
  if (simField && simField !== icaoKey(input.mission.destIcao)) return null;
  return dwellFromSimHistory(input.simHistory, input.mission.destIcao);
}

function ticketOperated(
  mission: Pick<Mission, "id" | "status" | "lifecycle" | "destIcao">,
  logs: ReadonlyArray<FlightLogEntry>,
): boolean {
  if (mission.status === "completed") return true;
  if (mission.lifecycle === "arrived" || mission.lifecycle === "diverted" || mission.lifecycle === "completed") {
    return true;
  }
  return logs.some((l) => l.tripId === mission.id && Boolean(l.arrivedAtUtc));
}

/**
 * Parking is a cost of a flight that happened. Schedule alone never posts it,
 * and the tail must have actually been on that ramp — now or before it left.
 */
export function parkingBlock(input: {
  mission: Pick<Mission, "id" | "status" | "lifecycle" | "destIcao" | "registration">;
  grounding: TicketGrounding;
  logs: ReadonlyArray<FlightLogEntry>;
  aircraft?: Pick<AircraftSpec, "id" | "registration" | "lastAirportIcao"> | null;
}): string {
  const field = icaoKey(input.mission.destIcao);
  const blocked = completionBlock(input.grounding);
  if (blocked) return blocked;
  if (!ticketOperated(input.mission, input.logs)) {
    return `${input.grounding.flightNumber} has not been flown — no ground dwell at ${field}`;
  }
  if (
    input.grounding.presence.known &&
    !fieldEverVisited({
      icao: field,
      presence: input.grounding.presence,
      logs: input.logs,
      aircraft: input.aircraft,
      registration: input.mission.registration,
    })
  ) {
    return `aircraft has not been on the ${field} ramp`;
  }
  return "";
}

export function quoteParkingForDwell(input: {
  catalogs?: MoneyCatalogs;
  icao: string;
  aircraft?: Pick<AircraftSpec, "icaoType">;
  dwell?: GroundDwell | null;
  /** Proposal / quote default — one day-rate. */
  estimateDays?: number;
}): { parkingCad: number; days: number; basis: "estimate" | "actual"; dwell: GroundDwell | null } {
  const dayRate = parkingDayRateCad(input.catalogs, input.icao, input.aircraft);
  if (input.dwell) {
    const days = parkingDaysFromDwellHours(input.dwell.hours);
    return {
      parkingCad: parkingCadFromDays(dayRate, days),
      days,
      basis: "actual",
      dwell: input.dwell,
    };
  }
  const days = input.estimateDays ?? 1;
  return {
    parkingCad: parkingCadFromDays(dayRate, days),
    days,
    basis: "estimate",
    dwell: null,
  };
}

export function reconcileParkingLedger(input: {
  account: CompanyAccount;
  missions: ReadonlyArray<Mission>;
  logs: ReadonlyArray<FlightLogEntry>;
  catalogs?: MoneyCatalogs;
  fleet?: ReadonlyArray<AircraftSpec>;
  simHistory?: ReadonlyArray<SimPhaseSample>;
  /** Live / last-known sim fix — grounds which field the dwell belongs to. */
  fused?: FusedPosition | null;
  scenery?: ReadonlyArray<SceneryAirport>;
  homeCurrency?: CurrencyCode;
  now?: Date;
}): { account: CompanyAccount; updatedMissionIds: string[]; blocked: GroundingHold[] } {
  const now = input.now ?? new Date();
  const home = hydrateCurrency(input.homeCurrency ?? input.catalogs?.currency ?? input.account.homeCurrency);
  let account = input.account;
  const updatedMissionIds: string[] = [];
  const blocked: GroundingHold[] = [];
  for (const mission of input.missions) {
    if (mission.status === "cancelled") continue;
    if (mission.flightType === "ferry" && !mission.ledgerPosted) continue;
    const posted = account.transactions.filter((t) => t.kind === "parking" && t.missionId === mission.id);
    const grounding = groundTicket({
      mission,
      fused: input.fused,
      scenery: input.scenery,
      simHistory: input.simHistory,
      fleet: input.fleet,
      logs: input.logs,
      missions: input.missions,
      now,
    });
    const block = parkingBlock({
      mission,
      grounding,
      logs: input.logs,
      aircraft: input.fleet?.find((a) => a.id === mission.aircraftId || a.registration === mission.registration),
    });
    if (block) {
      const voided = posted.length
        ? voidTripKindTxn(account, "parking", (t) => t.missionId === mission.id)
        : { account, removed: [] };
      account = voided.account;
      if (posted.length) {
        blocked.push({
          missionId: mission.id,
          flightNumber: grounding.flightNumber,
          kind: "parking",
          icao: icaoKey(mission.destIcao),
          reason: block,
          voided: voided.removed.length > 0,
          status: `PARKING VOID  ${grounding.flightNumber} ${icaoKey(mission.destIcao)} — ${block}`,
          atUtc: now.toISOString(),
        });
      }
      continue;
    }
    const dwell = resolveParkingDwell({
      mission,
      logs: input.logs,
      missions: input.missions,
      simHistory: input.simHistory,
      simAtIcao: grounding.presence.atIcao,
    });
    if (!dwell && !posted.length) continue;
    if (!dwell) continue;
    const aircraft = input.fleet?.find((a) => a.id === mission.aircraftId || a.registration === mission.registration);
    const quoted = quoteParkingForDwell({
      catalogs: input.catalogs,
      icao: mission.destIcao,
      aircraft,
      dwell,
    });
    const amountCad = -quoted.parkingCad;
    const memo = parkingMemo(mission.destIcao, dwell, quoted.days);
    const current = posted.reduce((sum, t) => sum + t.amountCad, 0);
    if (posted.length && Math.round(current * 100) === Math.round(amountCad * 100) && posted.every((t) => t.memo === memo)) {
      continue;
    }
    if (!posted.length && quoted.parkingCad <= 0) continue;
    account = replaceTripParkingTxn(
      account,
      {
        createdAtUtc: now.toISOString(),
        kind: "parking",
        amountCad,
        memo,
        missionId: mission.id,
        aircraftId: mission.aircraftId,
        currency: home,
        idempotencyKey: ledgerIdempotencyKey(mission.id, "parking"),
      },
      now,
    );
    updatedMissionIds.push(mission.id);
  }
  return { account, updatedMissionIds, blocked };
}
